import { Exercise, ExerciseInPlan, Prescription } from "../types";
import {
  ParsedPdfExercise,
  ParsedWorkoutPdf,
} from "./parseWorkoutPdfText";

export type CatalogExercise = Pick<Exercise, "id" | "name" | "image" | "image_url">;

export type MatchStats = {
  total: number;
  matched: number;
  unmatched: number;
};

export function normalizeExerciseName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Índice O(n) no catálogo; lookup O(1) por nome normalizado.
 */
export function buildExerciseNameIndex(
  catalog: CatalogExercise[],
): Map<string, CatalogExercise> {
  const index = new Map<string, CatalogExercise>();
  for (const ex of catalog) {
    if (!ex?.name) continue;
    const key = normalizeExerciseName(ex.name);
    if (key && !index.has(key)) {
      index.set(key, ex);
    }
  }
  return index;
}

function toExerciseInPlan(
  parsed: ParsedPdfExercise,
  catalogHit: CatalogExercise | undefined,
  professionalId: number,
): ExerciseInPlan {
  const sets = String(parsed.sets ?? "").trim();
  const reps = String(parsed.reps ?? "").trim();
  const load = String(parsed.load ?? "").trim();
  const interval = String(parsed.interval ?? "").trim();
  const observation = String(parsed.observation ?? "").trim();

  return {
    id: catalogHit?.id ? Number(catalogHit.id) : 0,
    name: catalogHit?.name || parsed.name,
    sets,
    reps,
    series: sets,
    repetitions: reps,
    load,
    interval,
    observation,
    professional: professionalId,
    image: catalogHit?.image,
    image_url: catalogHit?.image_url,
  } as ExerciseInPlan;
}

/**
 * Converte PDF parseado + catálogo em Prescription[] prontas para o formulário.
 * Complexidade ~ O(E + C) com mapa de nomes.
 */
export function matchImportedExercises(
  parsed: ParsedWorkoutPdf,
  catalog: CatalogExercise[],
  professionalId: number | string,
): { prescriptions: Prescription[]; stats: MatchStats; planName: string } {
  const proId = Number(professionalId) || 0;
  const index = buildExerciseNameIndex(catalog);
  let matched = 0;
  let total = 0;

  const prescriptions: Prescription[] = parsed.prescriptions.map((p, i) => ({
    name: p.name || `Treino ${String.fromCodePoint(65 + i)}`,
    observation: p.observation || "",
    exercises: p.exercises.map((ex) => {
      total += 1;
      const hit = index.get(normalizeExerciseName(ex.name));
      if (hit?.id) matched += 1;
      return toExerciseInPlan(ex, hit, proId);
    }),
  }));

  return {
    planName: parsed.planName,
    prescriptions,
    stats: {
      total,
      matched,
      unmatched: total - matched,
    },
  };
}
